<template>
  <page :loading="!Array.isArray(jobAlerts) || $apollo.loading">
    <profile-nav class="mb-3" v-if="isEnglishLang"/>
    <b-row>
      <b-col cols="12" md="8" offset-md="2">
        <b-card>
          <template v-if="!Array.isArray(jobAlerts) || $apollo.loading">
            <no-ssr><loading-spinner class="d-flex justify-content-center my-5"/></no-ssr>
          </template>
          <div class="my-3" v-else>
            <b-alert class="my-3 px-3" show>
              <strong>Configure your alerts</strong> to get emails &amp; other notifications for jobs you're interested in referring.
            </b-alert>

            <hr v-if="jobAlerts.length"/>

            <template v-for="(alert, i) in jobAlerts">
              <div class="d-flex justify-content-end align-items-center px-1" :key="`delete-${alert.id}`">
                <b-btn class="bg-transparent border-0 text-muted px-0" @click.prevent="() => deleteAlert(alert)">
                  <font-awesome-icon icon="times"/>
                </b-btn>
              </div>
              <configure-line class="mb-3" :key="`configure-${alert.id}`" :value="alert"
                :departments="departments" :customFields="customFields" @input="update => saveAlert(update)"/>
              <hr :key="`hr-${i}`" v-if="(i+1) < jobAlerts.length"/>
            </template>

            <hr v-if="jobAlerts.length"/>

            <div class="d-flex justify-content-start justify-content-sm-center align-items-center my-3">
              <b-btn @click.prevent="createAlert" variant="outline-primary">
                <font-awesome-icon icon="plus"/> New Alert
              </b-btn>
            </div>
          </div>
        </b-card>

      </b-col>
    </b-row>
  </page>
</template>

<script>
import gql from 'graphql-tag';
import Page from '@/components/Page';
import ProfileNav from '@/components/users/ProfileNav';
import ConfigureLine from '@/components/job-alerts/ConfigureLine';
import LoadingSpinner from '@/components/misc/LoadingSpinner';
import JobAlertDetailsFragment from '@src/graphql/fragments/JobAlertDetails.gql';
import { packSingleJSON, unpackSingleJSON } from '@src/lib/pack-single-json';
import { mapRoot } from '@src/mixins/root';

const GetUserAlertsQuery = gql`
  query GetUserAlerts {
    company {
      id
      departments {
        id
        name
        icon
      }
      preferences {
        jobAlertCustomFields
      }
    }
    user {
      id
      jobAlerts {
        ...JobAlertDetails
      }
    }
  }
  ${JobAlertDetailsFragment}
`;

export default {
  name: 'OnboardingAlerts',
  components: {
    Page,
    ProfileNav,
    LoadingSpinner,
    ConfigureLine,
  },
  data: () => ({
    departments: null,
    customFields: null,
  }),
  apollo: {
    jobAlerts: {
      query: GetUserAlertsQuery,
      update: ({ user: { jobAlerts } }) => (jobAlerts || []).map(alert => {
        alert.customFields = alert.customFields ? unpackSingleJSON(alert.customFields) : null;
        return alert;
      }),
      result({ loading, data }) {
        if (!loading && data && data.company) {
          const { departments, preferences: { jobAlertCustomFields: customFields } } = data.company;
          this.departments = departments || null;
          this.customFields = customFields ? unpackSingleJSON(customFields) : null;
        }
      },
    },
  },
  metaInfo: {
    title: 'Setup your alerts!',
  },
  computed: {
    ...mapRoot([ 'isEnglishLang' ]),
  },
  methods: {
    async createAlert() {
      try {
        await this.$apollo.mutate({
          mutation: gql`
            mutation CreateUserAlert {
              createJobAlert(create: {}) {
                ...JobAlertDetails
              }
            }
            ${JobAlertDetailsFragment}
          `,
          update(store, { data: { createJobAlert } }) {
            const data = store.readQuery({ query: GetUserAlertsQuery });
            data.user.jobAlerts = Array.isArray(data.user.jobAlerts) ? data.user.jobAlerts : [];
            data.user.jobAlerts.push(createJobAlert);
            store.writeQuery({ query: GetUserAlertsQuery, data });
          },
        });
      } catch (err) {
        this.$log.error(err);
      }
    },
    async saveAlert({ id: jobAlertID, department, location, customFields }) {
      try {
        await this.$apollo.mutate(jobAlertID ? {
          mutation: gql`
            mutation UpdateUserAlert($update: UpdateJobAlertInput!, $where: JobAlertWhereOneInput!) {
              updateJobAlert(update: $update, where: $where) {
                ...JobAlertDetails
              }
            }
            ${JobAlertDetailsFragment}
          `,
          variables: {
            update: { department, location, customFields: packSingleJSON(customFields) },
            where: { id: jobAlertID },
          },
        } : {
          mutation: gql`
            mutation CreateUserAlert($create: CreateJobAlertInput!) {
              createJobAlert(create: $create) {
                ...JobAlertDetails
              }
            }
            ${JobAlertDetailsFragment}
          `,
          variables: {
            create: { department, location, customFields: packSingleJSON(customFields) },
          },
          update(store, { data: { createJobAlert } }) {
            const data = store.readQuery({ query: GetUserAlertsQuery });
            const { jobAlerts } = data.user;
            data.user.jobAlerts = (Array.isArray(jobAlerts) ? jobAlerts : []).concat(createJobAlert);
            store.writeQuery({ query: GetUserAlertsQuery, data });
          },
        });
      } catch (err) {
        this.$log.error(err);
      }
    },
    async deleteAlert({ id: jobAlertID }) {
      try {
        await this.$apollo.mutate({
          mutation: gql`
            mutation DeleteUserAlert($jobAlertID: ID!) {
              deleteJobAlert(where: { id: $jobAlertID })
            }
          `,
          variables: {
            jobAlertID,
          },
          update(store) {
            const data = store.readQuery({ query: GetUserAlertsQuery });
            data.user.jobAlerts.splice(data.user.jobAlerts.findIndex(({ id }) => id === jobAlertID), 1);
            store.writeQuery({ query: GetUserAlertsQuery, data });
          },
        });
      } catch (err) {
        this.$log.error(err);
      }
    },
  }
};
</script>



// WEBPACK FOOTER //
// src/views/User/EditAlerts.vue